import { type TypescriptHelpersT } from '@elliemae/ds-typescript-helpers';
import React, { useCallback } from 'react';
import type { ControlledDateTimePickerContextT } from '../../config/useControlledDateTimePicker.js';
import { ControlledDateTimePickerDatatestid } from '../../constants/index.js';
import type { DSControlledDateTimePickerInternalsT } from '../../sharedTypes.js';
import { StyledYearInput } from '../Styleds.js';
import { convertToPositiveNumberIfPossible } from '../../utils/numberHelpers.js';
import { fillStringWithPlaceHolders } from '../../utils/stringHelpers.js';
const regExpNumbers = /^[0-9]+$/;
const isInvalidYear = (year: string) => {
  if (year === '') return false;
  const numberOnly = regExpNumbers.test(year);
  return year.length > 4 || !numberOnly || Number.parseInt(year, 10) > 9999;
};

interface YYYYInputsPropsT {
  onChange: DSControlledDateTimePickerInternalsT.OnChangeFunc;
  onBlur: DSControlledDateTimePickerInternalsT.OnBlurFunc;
  onFocus: DSControlledDateTimePickerInternalsT.OnFocusFunc;
  onKeyDown: DSControlledDateTimePickerInternalsT.OnKeyDown;
  value: string;
  disabled: boolean;
  applyAriaDisabled?: boolean;
  hasError: boolean;
  contextRef: React.MutableRefObject<HTMLInputElement | null>;
  tabIndex?: TypescriptHelpersT.WCAGTabIndex;
  placeholder: string;
  isFocused: boolean;
  getProps: ControlledDateTimePickerContextT['getProps'];
  readOnly: boolean;
}
export const YYYYInput = React.memo(
  ({
    onChange,
    onBlur,
    onFocus,
    onKeyDown,
    value,
    disabled,
    applyAriaDisabled,
    hasError,
    placeholder,
    contextRef,
    tabIndex,
    isFocused,
    getProps,
    readOnly,
  }: YYYYInputsPropsT): JSX.Element => {
    const validateOnChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        if (isInvalidYear(e?.currentTarget?.value)) {
          e.preventDefault();
          return;
        }
        onChange(e);
      },
      [onChange],
    );

    const yearNum = convertToPositiveNumberIfPossible(value);

    return (
      <StyledYearInput
        onChange={validateOnChange}
        onBlur={onBlur}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        value={value}
        innerRef={(DomElem: HTMLInputElement) => {
          (contextRef as React.MutableRefObject<HTMLInputElement>).current = DomElem;
          if (isFocused) DomElem?.focus?.();
        }}
        tabIndex={tabIndex}
        data-testid={ControlledDateTimePickerDatatestid.DATE_INPUTS.YEAR}
        placeholder={placeholder}
        aria-label="year"
        disabled={disabled}
        aria-disabled={applyAriaDisabled}
        aria-invalid={hasError}
        getOwnerProps={getProps}
        readOnly={readOnly}
        role="spinbutton"
        aria-valuemin={0}
        aria-valuemax={9999}
        // when empty, valuenow falls back to the min (not omitted) so VoiceOver honors
        // aria-valuetext="empty" instead of announcing a computed position percentage
        aria-valuenow={yearNum === -1 ? 0 : yearNum}
        aria-valuetext={yearNum === -1 ? 'empty' : fillStringWithPlaceHolders(`${yearNum}`, 4, '0')}
      />
    );
  },
);
